/**
 * PermissionsPanel Component
 * 
 * Main component for managing permissions on agents and collections.
 * Displays a table of current permissions with actions to add/remove.
 */

import { useState } from 'react';
import { 
  Button, 
  Skeleton,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
} from '@/components/ui';
import { useI18n } from '@/i18n';
import { usePermissions, useRevokePermission } from '../hooks/usePermissions';
import { PermissionRow } from './PermissionRow';
import { AddPermissionDialog } from './AddPermissionDialog';

interface PermissionsPanelProps {
  entityType: 'agent' | 'collection' | 'mcp_server';
  entityId: string;
  className?: string;
}

export function PermissionsPanel({ 
  entityType, 
  entityId,
  className,
}: PermissionsPanelProps) {
  const { t } = useI18n();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const { data: permissions, isLoading } = usePermissions(entityType, entityId);
  const revokePermission = useRevokePermission();

  if (isLoading) {
    return (
      <div className={className}>
        <div className="flex items-center justify-between mb-4">
          <Skeleton className="h-6 w-32" />
          <Skeleton className="h-9 w-32" />
        </div>
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const handleRevoke = (subjectId: string, subjectType: 'user' | 'group') => {
    revokePermission.mutate({
      entityType,
      entityId,
      subjectId,
      subjectType,
    });
  };

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100">
          {t('admin.permissions.title')}
        </h3>
        <Button 
          leftIcon="plus" 
          variant="outline"
          size="sm"
          onClick={() => setShowAddDialog(true)}
        >
          {t('admin.permissions.add')}
        </Button>
      </div>

      {!permissions || permissions.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400 border border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
          {t('admin.permissions.empty')}
        </div>
      ) : (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin.permissions.subject')}</TableHead>
                <TableHead>{t('admin.permissions.type')}</TableHead>
                <TableHead>{t('admin.permissions.level')}</TableHead>
                <TableHead className="w-24">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {permissions.map((p) => (
                <PermissionRow 
                  key={`${p.subjectType}-${p.subjectId}`}
                  permission={p}
                  onRevoke={() => handleRevoke(p.subjectId, p.subjectType)}
                  isRevoking={revokePermission.isPending}
                />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AddPermissionDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        entityType={entityType}
        entityId={entityId}
      />
    </div>
  );
}
